<template>
  <div class="monitor-container">
    <div class="map-section">
      <div class="map-wrapper">
        <div class="vehicle-monitor-board">
          <!-- 左上角搜索筛选区域 -->
          <div class="search-filter-area">
            <div class="search-box">
              <div class="search-input-wrapper">
                <div class="search-input-inner">
                  <input
                      v-model="searchForm.sk"
                      type="text"
                      placeholder="请输入牌照或车架号搜索"
                      class="search-input"
                      @keyup.enter="handleSearch"
                  >
                  <span v-if="searchForm.sk" class="search-clear" @click="clearKeyword">×</span>
                </div>
                <a-select
                  v-model:value="searchForm.deptId"
                  style="width: 150px"
                  class="company-select"
                  placeholder="请选择主体"
                  allow-clear
                  @change="handleSearch"
                >
                  <a-select-option value="">全部</a-select-option>
                  <a-select-option v-for="company in companyList" :key="company.deptId" :value="company.id">
                    {{ company.name }}
                  </a-select-option>
                </a-select>
                <button class="search-btn" @click="handleSearch">搜索</button>
              </div>
            </div>
          </div>

          <!-- 车辆列表区域 -->
          <div class="vehicle-list-area">
            <div class="vehicle-list-header">
              <span class="vehicle-list-title">车辆列表</span>
<!--              <span class="vehicle-count">({{ vehicles.length }} 辆)</span>-->
            </div>
            <div class="vehicle-list">
              <div
                  v-for="(vehicle, index) in vehicles"
                  :key="vehicle.testingTaskId || index"
                  class="vehicle-item"
                  :class="{ 
                    'vehicle-item-selected': selectedVehicleId === vehicle.vehicleId
                  }"
                  @click="console.log('点击车辆:', vehicle); highlightVehicleOnMap(vehicle)"
              >
                <span class="business-status-badge" :class="getBusinessStatusClass(vehicle.businessStatus)">
                  {{ vehicle.businessStatusName || getBusinessStatusText(vehicle.businessStatus) }}
                </span>
                <div class="vehicle-info">
                  <div class="vehicle-main-info">
                    <span class="vehicle-type">{{ vehicle.deptName }}</span>
                    <span class="testing-number">{{ vehicle.licenceNo || (vehicle.vin ? vehicle.vin.slice(-6) : '') }}</span>
                  </div>
                  <div class="vehicle-route">
                    <div class="vehicle-status-text">
                      <span class="status-dot" :class="vehicleOnlineStatus[vehicle.vehicleId] === 1 ? 'status-online' : 'status-offline'"></span>
                      {{ vehicleOnlineStatus[vehicle.vehicleId] === 1 ? '在线' : '离线' }}
                    </div>
                    <div class="vehicle-operation-org">
                      <a-tooltip placement="bottom">
                        <template #title>
                          {{ vehicle.currentStopName || '-' }}
                        </template>
                        <span class="operation-org-name">目的地：{{ vehicle.currentStopName || '-' }}</span>
                      </a-tooltip>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- 分页组件 -->
            <div class="vehicle-pagination">
              <a-pagination
                  v-model:current="pagination.current"
                  v-model:page-size="pagination.pageSize"
                  :total="pagination.total"
                  :show-size-changer="pagination.showSizeChanger"
                  :page-size-options="pagination.pageSizeOptions"
                  :show-total="pagination.showTotal"
                  @change="handlePageChange"
                  @showSizeChange="handlePageChange"
              />
            </div>
          </div>

          <!-- 右上角信息显示区域 -->
          <div class="info-display-area">
            <div class="area-info-box">
              <div class="test-area-vehicle-info-item">
                <label class="test-area-label">区域:</label>
                <a-select 
                  v-model:value="selectedTestArea"
                  style="width: 150px" 
                  class="test-area-select"
                  @change="handleTestAreaChange"
                  :disabled="testAreaList.length === 0"
                  :key="selectKey"
                >
                  <a-select-option v-for="area in testAreaList" :key="area.id" :value="String(area.id)">
                    {{ area.name }}
                  </a-select-option>
                </a-select>
              </div>
              <span class="separator">|</span>
              <div class="info-board-stats">
                <div class="board-stat-item">
                  <span class="board-status-dot online-dot"></span>
                  <span class="board-stat-label">在线车辆:</span>
                  <span class="board-stat-value">{{ onlineVehicleCount }}</span>
                </div>
                <span class="separator">|</span>
                <div class="board-stat-item">
                  <span class="board-status-dot error-dot"></span>
                  <span class="board-stat-label">异常告警:</span>
                  <span class="board-stat-value">{{ errorVehicleCount }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 事件区域 -->
          <div class="vehicle-events-container">
            <div class="event-item fault-event" @click.stop="openAlarmModal('1')">
              <span class="event-icon">⚠️</span>
              <div class="event-content">
                <span class="event-label">故障</span>
                <span class="event-count">{{ selectedVehicleInfo?.faultEventCount || 0 }}</span>
              </div>
            </div>
            <div class="event-item abnormal-event" @click.stop="openAlarmModal('2')">
              <span class="event-icon">⚠️</span>
              <div class="event-content">
                <span class="event-label">异常</span>
                <span class="event-count">{{ selectedVehicleInfo?.abnormalEventCount || 0 }}</span>
              </div>
            </div>
          </div>

          <!-- 车辆实时视频区域 -->
          <div v-if="selectedVehicleInfo" class="vehicle-video-container">
            <!-- 前方视频播放器 -->
            <div class="front-video-player">
              <!-- 视频播放区域 -->
              <div class="video-playback-area">
                <!-- 单个视频元素，使用 flv.js 播放 -->
                <video
                  ref="videoRef"
                  class="video-player"
                  controls
                  muted
                >
                </video>
                <div class="video-indicator">
                  <span class="video-position">{{ getVideoPositionName() }}视角</span>
                  <span class="video-status">实时</span>
                </div>
              </div>
            </div>
          </div>
          <!-- 车辆信息显示区域 -->
          <div v-if="selectedVehicleInfo" class="vehicle-info-container">
            <!-- 车辆基本信息 -->
            <div class="vehicle-info-section">
              <div class="vehicle-details">
                <!-- 左侧车辆图片，右上角显示是否在线 -->
                <div class="vehicle-image-container">
                  <div class="online-status-indicator" :class="{ 'online': selectedVehicleInfo.status === 1, 'offline': selectedVehicleInfo.status !== 1 }">
                    {{ selectedVehicleInfo.status === 1 ? '正常' : '离线' }}
                  </div>
                  <img :src="require('@/assets/images/wurenche_img.jpeg')" :alt="selectedVehicleInfo.vehicleType || '车辆图片'" class="vehicle-image" />
                </div>
                <!-- 右侧车辆信息 -->
                <div class="vehicle-info-content">
                  <div class="vehicle-info-item">
                    <div class="vehicle-info-value-plate">{{ selectedVehicleInfo.licenceNo || (selectedVehicleInfo.vin ? selectedVehicleInfo.vin.slice(-6) : '') }}</div>
                  </div>
                  <div class="vehicle-info-item">
                    <div class="vehicle-info-value">车架号：{{ selectedVehicleInfo.vin }}</div>
                  </div>
                  <div class="speed-mode-container">
                    <div class="vehicle-speed-box">
                      <div class="speed-value">
                        {{ selectedVehicleInfo?.speed || 0 }}
                        <span class="speed-unit">km/h</span>
                      </div>
                      <div class="speed-label">时速</div>
                    </div>
                    <div class="vehicle-mode-box">
                      <div class="mode-value" :class="{
                        'mode-auto': selectedVehicleInfo?.drivingMode === 1,
                        'mode-manual': selectedVehicleInfo?.drivingMode === 2,
                        'mode-remote': selectedVehicleInfo?.drivingMode === 3
                      }">
                        {{ getDrivingModeView(selectedVehicleInfo?.drivingMode) }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 测试路线区域 -->
            <div class="vehicle-operation-section route-section">
              <div class="route-info">
                <!-- 测试主体信息 -->
                <div class="info-item">
                  <span class="info-label">主体企业：</span>
                  <span class="info-value">{{ selectedVehicleInfo.providerName }}</span>
                </div>
                <!-- 安全员和司机信息 -->
                <template v-if="selectedVehicleInfo?.securityUsers && selectedVehicleInfo.securityUsers.length > 0">
                  <div v-for="securityUser in selectedVehicleInfo.securityUsers" :key="securityUser.id" class="info-item">
                    <span class="info-label">{{ securityUser.type === 'security' ? '安全员' : securityUser.type === 'driver' ? '司机' : securityUser.type }}：</span>
                    <span class="info-value">{{ securityUser.name }} {{ securityUser.phone }}</span>
                  </div>
                </template>
                <div v-else class="info-item">
                  <span class="info-label">安全员：</span>
                  <span class="info-value">暂无数据</span>
                </div>
                <!-- 测试主体信息 -->
                <div class="info-item">
                  <span class="info-label">运营公司：</span>
                  <span class="info-value">{{ selectedVehicleInfo.operatorName }}</span>
                </div>
<!--                <button class="view-details-btn" @click="viewVehicleTestingDetails">-->
<!--                  查看详情-->
<!--                </button>-->
              </div>
            </div>

            <!-- 操作区域 -->
            <div class="vehicle-operation-section operation-section">
              <div class="operation-area">
                <!-- 左侧联系客服按钮 -->
                <div class="left-operation">
                  <button class="contact-service-btn" @click="contactCustomerService">
                    <img src="../../../assets/icons/kefu.png" alt="客服" class="service-icon" />
                  </button>
                </div>
                <!-- 右侧各视角按钮 -->
                <div class="right-operation">
<!--                  <div class="video-position-row">-->
<!--                    <button-->
<!--                        class="video-position-btn"-->
<!--                        :class="{ active: selectedVideoPosition === 'interior' }"-->
<!--                        @click="switchVideoPosition('interior')"-->
<!--                    >-->
<!--                      车内-->
<!--                    </button>-->
<!--                    <button-->
<!--                        class="video-position-btn"-->
<!--                        :class="{ active: selectedVideoPosition === 'pedals' }"-->
<!--                        @click="switchVideoPosition('pedals')"-->
<!--                    >-->
<!--                      踏板-->
<!--                    </button>-->
<!--                  </div>-->
                  <div class="video-position-center">
                    <button
                        class="video-position-btn"
                        :class="{ active: selectedVideoPosition === 'front' }"
                        @click="switchVideoPosition('front')"
                    >
                      前方
                    </button>
                  </div>
                  <div class="video-position-row">
                    <button
                        class="video-position-btn"
                        :class="{ active: selectedVideoPosition === 'left' }"
                        @click="switchVideoPosition('left')"
                    >
                      左侧
                    </button>
                    <button
                        class="video-position-btn"
                        :class="{ active: selectedVideoPosition === 'right' }"
                        @click="switchVideoPosition('right')"
                    >
                      右侧
                    </button>
                  </div>
                  <div class="video-position-center">
                    <button
                        class="video-position-btn"
                        :class="{ active: selectedVideoPosition === 'back' }"
                        @click="switchVideoPosition('back')"
                    >
                      后方
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 加载状态遮罩 -->
        <div v-if="loading" class="loading-overlay">
          <div class="loading-spinner"></div>
        </div>

        <!-- 错误提示 -->
        <div v-else-if="errorMessage" class="error-overlay">
          <div class="error-content">
            <div class="error-icon">⚠️</div>
            <h3>加载失败</h3>
            <p>{{ errorMessage }}</p>
            <button class="retry-btn" @click="drawMap">重试</button>
          </div>
        </div>

        <!-- 地图容器 -->
        <div id="monitor-map-container" class="map-container"></div>
      </div>
    </div>
    
    <!-- 告警信息列表弹框 -->
    <AlarmModal
      :visible="alarmModalVisible"
      :alarm-type="currentAlarmType"
      @update:visible="alarmModalVisible = $event"
    />
  </div>
</template>

<script setup>

import { ref, reactive, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { getAreaList } from '@/api/roads/routes.js';
import { getCompanyList } from '@/api/enterprise';
import { getVehiclePageList, getVehicleInfo } from '@/api/vehicle';
import { getCurrentTaskByVehcileId } from '@/api/task/task.js';
import { getStartPlay } from '@/api/video';
import { getVehicleOnlineStatus, countVehicleOnlineStatus } from '@/api/realtime/vehicleStatus';
import { getVehicleBusiness } from '@/api/business/vehicleBusiness';
import AMapLoader from '@amap/amap-jsapi-loader';
import carIcon from '@/assets/images/wcar.png';
import carSelect from '@/assets/images/wcar_select.png';
import { getDrivingModeView } from "@/utils/EnumUtils";
import flvjs from 'flv.js'
import AlarmModal from './components/alarmModal.vue';

const viewVehicleTestingDetails = () => {
  const vehicleId = selectedVehicleInfo.value.id;
  window.open(`/supervise/dynamic/detail?vehicleId=${encodeURIComponent(vehicleId)}`, '_blank');
}

// DOM 引用
const videoRef = ref(null)
// 播放器实例引用
let player = null

// 获取视频播放地址
const getVideoPlayUrl = (vid, position) => {
  return new Promise((resolve, reject) => {
    const param = {
      vehicleId: vid,
      position: position
    };
    getStartPlay(param).then(res => {
      if (res.code === 200) {
        resolve(res.data);
      } else {
        resolve('');
      }
    }).catch(error => {
      console.error('获取播放地址失败:', error);
      resolve('');
    });
  });
};

// 初始化播放器（带重试机制）
const initPlayerWithRetry = async () => {
  // 重置重试计数
  videoRetryCount.value = 0;
  // 执行初始化
  await doInitPlayer();
};

// 实际执行播放器初始化
const doInitPlayer = async () => {
  // 如果正在加载，跳过
  if (!videoRef.value) {
    console.error('视频元素未准备好，稍后重试');
    return;
  }

  // 确保车辆信息存在
  if (!selectedVehicleInfo.value || !selectedVehicleInfo.value.vehicleId) {
    console.error('车辆ID不存在，无法获取视频播放地址');
    return;
  }

  clearPlayWatchdog();

  try {
    // 1. 获取视频播放地址（每次都通过接口获取最新链接）
    let currentVideoUrl = await getVideoPlayUrl(selectedVehicleInfo.value.vehicleId, selectedVideoPosition.value);
    
    if (!currentVideoUrl) {
      console.error('未找到当前视角的视频地址');
      // 销毁旧播放器，清空视频画面
      if (player) {
        try {
          player.pause();
          player.unload();
          player.detachMediaElement();
          player.destroy();
        } catch (e) {
          console.warn('销毁旧播放器时出错:', e);
        }
        player = null;
      }
      // 清空视频元素的src，停止播放
      if (videoRef.value) {
        videoRef.value.src = '';
        videoRef.value.load();
      }
      videoLoading.value = false;
      return;
    }

    // 2. 销毁旧的播放器实例
    if (player) {
      try {
        player.pause();
        player.unload();
        player.detachMediaElement();
        player.destroy();
      } catch (e) {
        console.warn('销毁旧播放器时出错:', e);
      }
      player = null;
    }

    // 3. 等待一小段时间确保DOM完全释放
    await new Promise(resolve => setTimeout(resolve, 150));

    // 4. 确保视频元素仍然存在
    if (!videoRef.value) {
      console.error('视频元素已不存在');
      return;
    }

    // 清理视频元素残留状态，避免复用失效的 MediaSource 导致首次加载失败
    try {
      videoRef.value.pause();
      videoRef.value.removeAttribute('src');
      videoRef.value.load();
    } catch (e) {
      console.warn('重置视频元素时出错:', e);
    }

    // 5. 创建 flv.js 播放器实例
    player = flvjs.createPlayer({
      type: 'flv',
      isLive: true,
      url: currentVideoUrl,
      cors: true,
      hasAudio: false,
      hasVideo: true,
      headers: {
        'Referer': window.location.origin
      }
    }, {
      enableWorker: false,
      enableStashBuffer: false,
      stashInitialSize: 128,
      lazyLoad: true,
      lazyLoadMaxDuration: 3,
      autoCleanupSourceBuffer: true,
      fixAudioTimestampGap: true,
      isLive: true,
      referrerPolicy: 'no-referrer-when-downgrade'
    });

    // 6. 绑定DOM元素
    player.attachMediaElement(videoRef.value);

    // 播放状态标记，用于避免重复播放
    let playTriggered = false;
    
    // 7. 添加事件监听
    player.on(flvjs.Events.ERROR, (errorType, errorDetail) => {
      console.error('播放出错:', errorType, errorDetail);
      clearPlayWatchdog();
      // 发生错误时尝试重试
      handlePlayerRetry();
    });

    player.on(flvjs.Events.LOADING_COMPLETE, () => {
      console.log('视频加载完成');
      clearPlayWatchdog();
      videoLoading.value = false;
      videoRetryCount.value = 0;
    });

    player.on(flvjs.Events.STATISTICS_INFO, (info) => {
      console.log('播放统计信息:', info);
    });

    // 添加 MEDIA_INFO 事件，获取到媒体信息后尝试播放
    player.on(flvjs.Events.MEDIA_INFO, () => {
      console.log('获取到媒体信息');
      if (!playTriggered && player) {
        playTriggered = true;
        triggerPlay();
      }
    });

    // 添加 FIRST_FRAME_RENDERED 事件，确保第一帧渲染后播放状态正常
    player.on(flvjs.Events.FIRST_FRAME_RENDERED, () => {
      console.log('第一帧已渲染');
      clearPlayWatchdog();
      videoLoading.value = false;
      videoRetryCount.value = 0;
    });

    // 添加 CAN_PLAY 事件
    player.on(flvjs.Events.CAN_PLAY, () => {
      console.log('视频可以播放');
      if (!playTriggered && player) {
        playTriggered = true;
        triggerPlay();
      }
    });

    // 8. 加载视频
    player.load();

    // 启动首帧超时监控：若流卡住不触发 ERROR，也能自动重新加载
    startPlayWatchdog();
    
    // 9. 立即尝试播放，同时等待事件触发
    playTriggered = true;
    triggerPlay();

  } catch (error) {
    console.error('初始化播放器失败:', error);
    await handlePlayerRetry();
  }
};

// 触发播放的辅助函数
const triggerPlay = () => {
  if (!player) return;
  
  player.play().then(() => {
    console.log('视频播放成功');
    clearPlayWatchdog();
    videoLoading.value = false;
    videoRetryCount.value = 0;
  }).catch(err => {
    console.warn('自动播放被阻止或播放出错:', err);
    // 尝试重新播放
    setTimeout(() => {
      if (player) {
        player.play().catch(e => {
          console.error('重试播放失败:', e);
          handlePlayerRetry();
        });
      }
    }, 1000);
  });
};

// 处理播放器重试
const handlePlayerRetry = async () => {
  if (videoRetryCount.value >= maxRetryCount) {
    console.error('已达到最大重试次数，停止重试');
    videoLoading.value = false;
    return;
  }

  videoRetryCount.value++;
  console.log(`第 ${videoRetryCount.value} 次重试...`);
  
  // 等待一段时间后重试
  await new Promise(resolve => setTimeout(resolve, 2000 * videoRetryCount.value));
  
  // 重新初始化播放器
  await doInitPlayer();
};

// 初始化播放器（保持原有的简单版本用于初始加载）
const initPlayer = async () => {
  videoLoading.value = true;
  videoRetryCount.value = 0;
  
  if (!flvjs.isSupported()) {
    console.error('当前浏览器不支持 flv.js 播放 FLV 格式');
    videoLoading.value = false;
    return;
  }

  if (!videoRef.value) {
    console.error('视频元素未准备好，稍后重试');
    await nextTick();
    if (!videoRef.value) {
      console.error('视频元素仍然未准备好');
      videoLoading.value = false;
      return;
    }
  }

  if (player) {
    player.pause();
    player.unload();
    player.detachMediaElement();
    player.destroy();
    player = null;
  }

  let currentVideoUrl = '';
  if (!selectedVehicleInfo.value || !selectedVehicleInfo.value.vehicleId) {
    console.error('车辆ID不存在，无法获取视频播放地址');
    videoLoading.value = false;
    return;
  } else {
    currentVideoUrl = await getVideoPlayUrl(selectedVehicleInfo.value.vehicleId, selectedVideoPosition.value);
    console.log("从接口获取视频地址:", currentVideoUrl);
  }
  
  if (!currentVideoUrl) {
    console.error('未找到当前视角的视频地址');
    // 销毁旧播放器，清空视频画面
    if (player) {
      try {
        player.pause();
        player.unload();
        player.detachMediaElement();
        player.destroy();
      } catch (e) {
        console.warn('销毁旧播放器时出错:', e);
      }
      player = null;
    }
    // 清空视频元素的src，停止播放
    if (videoRef.value) {
      videoRef.value.src = '';
      videoRef.value.load();
    }
    videoLoading.value = false;
    return;
  }

  player = flvjs.createPlayer({
    type: 'flv',
    isLive: true,
    url: currentVideoUrl,
    cors: true,
    hasAudio: false,
    hasVideo: true,
    headers: {
      'Referer': window.location.origin
    }
  }, {
    enableWorker: false,
    enableStashBuffer: false,
    stashInitialSize: 128,
    lazyLoad: true,
    lazyLoadMaxDuration: 3,
    autoCleanupSourceBuffer: true,
    fixAudioTimestampGap: true,
    isLive: true,
    referrerPolicy: 'no-referrer-when-downgrade'
  });

  player.attachMediaElement(videoRef.value);
  player.load();

  player.play().then(() => {
    console.log('视频播放成功');
    videoLoading.value = false;
  }).catch(err => {
    console.warn('自动播放被阻止或播放出错:', err);
    setTimeout(() => {
      if (player) {
        player.play().catch(e => {
          console.error('重试播放失败:', e);
          videoLoading.value = false;
        });
      }
    }, 1000);
  });

  player.on(flvjs.Events.ERROR, (errorType, errorDetail) => {
    console.error('播放出错:', errorType, errorDetail);
  });

  player.on(flvjs.Events.STATISTICS_INFO, (info) => {
    console.log('播放统计信息:', info);
  });
}

const destroyPlayer = () => {
  clearPlayWatchdog()
  if (player) {
    player.pause()
    player.unload()
    player.detachMediaElement()
    player.destroy()
    player = null
  }
}

// 地图实例
const map = ref(null);
let AMapInstance = null; // 高德地图 API 类引用
const polygons = ref([]);
const polylines = ref([]);
const markers = ref([]); // 添加缺失的markers ref声明以修复ReferenceError错误
// 当前选中车辆的任务路线覆盖物集合，切换选中时需先清除
const taskRouteOverlays = [];

// 内部响应式变量存储地图数据
const mapData = ref(null); // 存储地图数据
const areaDescription = ref(null);
const loading = ref(false);
const errorMessage = ref(null);
const currentMapStyle = ref('amap://styles/whitesmoke');

// 搜索筛选相关状态
const searchForm = ref({
  sk: '', // 车架号
  deptId: '' // 车企
});

// 当前选中的测试任务id
const selectedVehicleId = ref('');

// 当前选中的测试任务信息
const selectedVehicleInfo = ref(null);

// 告警弹框相关
const alarmModalVisible = ref(false);
const currentAlarmType = ref('');

const openAlarmModal = (type) => {
  currentAlarmType.value = type;
  alarmModalVisible.value = true;
};
// 当前选中的视频位置
const selectedVideoPosition = ref('front'); // 默认为前方
// 视频加载状态
const videoLoading = ref(false);
// 视频加载重试次数
const videoRetryCount = ref(0);
const maxRetryCount = 3;
// 首帧超时看门狗：流卡住时不会触发 ERROR 事件，需要主动超时重载
let playWatchdogTimer = null;
const PLAY_TIMEOUT = 10000;

const clearPlayWatchdog = () => {
  if (playWatchdogTimer) {
    clearTimeout(playWatchdogTimer);
    playWatchdogTimer = null;
  }
};

const startPlayWatchdog = () => {
  clearPlayWatchdog();
  playWatchdogTimer = setTimeout(() => {
    console.warn('视频首帧加载超时，重新加载播放器');
    handlePlayerRetry();
  }, PLAY_TIMEOUT);
};

// 切换视频位置
const switchVideoPosition = async (position) => {
  // 如果正在加载，取消当前加载
  videoLoading.value = true;
  videoRetryCount.value = 0;
  selectedVideoPosition.value = position;
  
  // 等待DOM更新完成
  await nextTick();
  
  // 重新初始化播放器，确保使用正确的视频源
  await initPlayerWithRetry();
};

// 获取视频位置名称
const getVideoPositionName = () => {
  const positionMap = {
    front: '前方',
    interior: '车内',
    pedals: '踏板',
    left: '左侧',
    right: '右侧',
    back: '后方'
  };
  return positionMap[selectedVideoPosition.value] || '前方';
};


// 联系客服
const contactCustomerService = () => {
  // 这里可以实现联系客服的逻辑
  console.log('联系客服')
};

// 信息显示区域相关状态
const selectedTestArea = ref(''); // 选中的测试区域
const onlineVehicleCount = ref(0); // 在线车辆数
const errorVehicleCount = ref(0); // 异常车辆数
const testAreaList = ref([]); // 测试区域列表
const companyList = ref([]); // 企业列表（测试主体）
const selectKey = ref(0); // 用于强制刷新 Select 组件

// 获取业务状态文本
const getBusinessStatusText = (status) => {
  const statusMap = {
    0: '空闲',
    1: '送货',
    2: '回站'
  };
  return statusMap[status] || '空闲';
};

// 获取业务状态样式类
const getBusinessStatusClass = (status) => {
  const classMap = {
    0: 'status-idle',
    1: 'status-delivering',
    2: 'status-returning'
  };
  return classMap[status] || 'status-idle';
};

// 获取车辆业务数据
const fetchVehicleBusiness = async (vehicle) => {
  const vehicleId = vehicle.vehicleId;
  try {
    const response = await getVehicleBusiness(vehicleId);
    if (response.code === 200 && response.data) {
      vehicle.businessStatusName = response.data.businessStatusName || '空闲';
      vehicle.currentStopName = response.data.currentStopName || '-';
    }
  } catch (error) {
    console.error(`获取车辆 ${vehicle.licenceNo} 业务数据失败:`, error);
    vehicle.businessStatusName = vehicle.businessStatusName || '空闲';
    vehicle.currentStopName    = vehicle.currentStopName || '-';
  }
};

// 批量获取业务数据
const fetchAllBusinessData = async () => {
  for (const vehicle of vehicles.value) {
    await fetchVehicleBusiness(vehicle);
    await new Promise(resolve => setTimeout(resolve, 100));
  }
};

let businessUpdateTimer = null;

// 获取测试区域列表
const fetchTestAreas = async () => {
  try {
    const res = await getAreaList();
    if (res.code === 200) {
      testAreaList.value = res.data || [];
      // 如果只有一个区域，立即同步设置选中值并强制刷新 Select 组件
      if (testAreaList.value.length === 1) {
        const areaId = String(testAreaList.value[0].id);
        selectedTestArea.value = areaId;
        await nextTick();
        selectKey.value++;
      }
    }
  } catch (error) {
    console.error('获取测试区域列表失败:', error);
  }
}

// 检查是否需要自动选中并触发区域切换（在地图初始化后调用）
const checkAutoSelectArea = () => {
  if (testAreaList.value.length === 1 && map.value) {
    handleTestAreaChange(selectedTestArea.value);
  }
}

// 获取企业列表（测试主体）
const fetchCompanyListData = async () => {
  try {
    const res = await getCompanyList();
    if (res.code === 200) {
      companyList.value = res.data || [];
    }
  } catch (error) {
    console.error('获取企业列表失败:', error);
  }
}

// 绘制单个测试区域
const drawTestArea = (area) => {
  if (!area || !area.geoJson) return;

  try {
    const geoJson = area.geoJson;

    // 检查是否有坐标数据
    if (geoJson.coordinates && Array.isArray(geoJson.coordinates) && geoJson.coordinates.length > 0) {
      const coordinates = geoJson.coordinates;
      const geoType = geoJson.type || 'polygon'; // 默认类型为polygon

      // 使用地图的setFitView方法聚焦到该区域并绘制边界
      if (map.value) {
        let overlay;
        
        // 根据类型创建不同的覆盖物
        if (geoType === 'line') {
          // 创建折线
          overlay = new window.AMap.Polyline({
            path: coordinates,
            strokeColor: '#999999', // 灰色
            strokeWeight: 1.4,
            strokeOpacity: 0.8,
            strokeStyle: 'dashed' // 不连续线段
          });
          // 添加到折线数组
          polylines.value.push(overlay);
        } else if (geoType === 'polygon') {
          // 创建多边形
          overlay = new window.AMap.Polygon({
            path: coordinates,
            strokeColor: '#999999', // 灰色
            strokeWeight: 1.4,
            strokeOpacity: 0.8,
            strokeStyle: 'dashed', // 不连续线段
            fillOpacity: 0 // 移除区域内的覆盖颜色
          });
          // 添加到多边形数组
          polygons.value.push(overlay);
        }

        // 如果成功创建了覆盖物，添加到地图
        if (overlay) {
          map.value.add(overlay);
        }
      }
    }
  } catch (error) {
    console.error(`解析测试区域${area.id}的geoJson失败:`, error);
  }
}

// 绘制所有测试区域
const drawAllTestAreas = () => {
  if (!map.value || !testAreaList.value || testAreaList.value.length === 0) return;

  // 清除之前绘制的所有覆盖物
  clearAllOverlays();

  // 遍历所有测试区域并绘制
  testAreaList.value.forEach(area => {
    drawTestArea(area);
  });
}

// 处理测试区域切换事件
const handleTestAreaChange = (areaId) => {
  if (!areaId) {
    // 如果选择"全部"，显示所有测试区域
    drawAllTestAreas();
    return;
  }

  // 找到选中的测试区域
  const selectedArea = testAreaList.value.find(area => String(area.id) === String(areaId));
  if (!selectedArea || !selectedArea.geoJson) return;

  try {

    const geoJson = selectedArea.geoJson;

    // 检查是否有坐标数据
    if (geoJson.coordinates && Array.isArray(geoJson.coordinates) && geoJson.coordinates.length > 0) {
      const coordinates = geoJson.coordinates;
      const geoType = geoJson.type || 'polygon'; // 默认类型为polygon

      // 使用地图的setFitView方法聚焦到该区域，但不绘制边界
      if (map.value) {
        // 根据坐标类型创建临时覆盖物用于计算视野范围，但不添加到地图
        let tempOverlay;
        
        // 根据类型创建不同的临时覆盖物
        if (geoType === 'line') {
          // 创建临时折线用于计算视野
          tempOverlay = new window.AMap.Polyline({
            path: coordinates
          });
        } else if (geoType === 'polygon') {
          // 创建临时多边形用于计算视野
          tempOverlay = new window.AMap.Polygon({
            path: coordinates
          });
        }

        // 如果成功创建了临时覆盖物，使用它来调整视野，然后销毁
        if (tempOverlay) {
          map.value.setFitView([tempOverlay]);
          // 销毁临时覆盖物
          tempOverlay = null;
        }
      }
    }
  } catch (error) {
    console.error('解析geoJson失败:', error);
  }
}

// 清除所有绘制的覆盖物（多边形和折线）
const clearAllOverlays = () => {
  if (!map.value) return;
  
  // 清除所有多边形
  polygons.value.forEach(polygon => {
    if (polygon) {
      map.value.remove(polygon);
    }
  });
  polygons.value = [];
  
  // 清除所有折线
  polylines.value.forEach(polyline => {
    if (polyline) {
      map.value.remove(polyline);
    }
  });
  polylines.value = [];
}

// 车辆列表分页数据
const vehicles = ref([]); // 当前页的车辆列表
const vehicleOnlineStatus = reactive({}); // 存储车辆在线状态，key为vehicleId，value为0(离线)或1(在线)
const pagination = reactive({
  current: 1, // 当前页码
  pageSize: 10, // 每页条数
  total: 0, // 总记录数
  showSizeChanger: false, // 是否显示每页条数选择器
  pageSizeOptions: ['10', '20', '50'], // 每页条数选项
  showTotal: (total) => `共 ${total} 辆` // 显示总数
});
let onlineStatusTimer = null; // 在线状态刷新定时器

// 获取车辆列表数据
const fetchVehicleList = async (page = 1, pageSize = 10) => {
  try {
    const conditionMap = {
      ...searchForm.value
    };
    
    const params = {
      pageIndex: page,
      pageSize: pageSize,
      conditionMap: conditionMap
    };

    const res = await getVehiclePageList(params);
    if (res.code === 200) {
      vehicles.value = res.data.records || [];
      pagination.total = res.data.total || 0;
      pagination.current = page;
      pagination.pageSize = pageSize;
      
      // 获取车辆在线状态
      await fetchVehicleOnlineStatus();
      
      // 获取业务数据
      await fetchAllBusinessData();
    }
  } catch (error) {
    console.error('获取车辆列表失败:', error);
  }
}

// 分页变化处理
const handlePageChange = (page, pageSize) => {
  fetchVehicleList(page, pageSize);
};

// 获取车辆在线状态
const fetchVehicleOnlineStatus = async () => {
  try {
    // 遍历车辆列表，获取每个车辆的在线状态
    for (const vehicle of vehicles.value) {
      if (vehicle.vehicleId) {
        const res = await getVehicleOnlineStatus(vehicle.vehicleId);
        if (res.code === 200) {
          vehicleOnlineStatus[vehicle.vehicleId] = res.data; // 0 离线，1 在线
        }
      }
    }
  } catch (error) {
    console.error('获取车辆在线状态失败:', error);
  }
};

// 启动在线状态定时刷新
const startOnlineStatusRefresh = () => {
  if (onlineStatusTimer) {
    clearInterval(onlineStatusTimer);
  }
  // 每30秒刷新一次在线状态和在线车辆数量
  onlineStatusTimer = setInterval(async () => {
    await fetchVehicleOnlineStatus();
    await updateVehicleStats();
  }, 30000);
};

// 停止在线状态定时刷新
const stopOnlineStatusRefresh = () => {
  if (onlineStatusTimer) {
    clearInterval(onlineStatusTimer);
    onlineStatusTimer = null;
  }
};

// 搜索功能 - 合并到分页搜索
const handleSearch = () => {
  console.log('搜索条件:', searchForm.value);
  // 重置页码，从第一页开始搜索
  pagination.current = 1;
  fetchVehicleList(1, pagination.pageSize);
  // 搜索完成后可以在地图上标记匹配的车辆位置
}

// 清空搜索关键字
const clearKeyword = () => {
  searchForm.value.sk = '';
  handleSearch();
}

// WebSocket相关状态
const ws = ref(null);
const wsConnected = ref(false);
const wsUrl = ref(''); // WebSocket服务器地址
const lastMessageTime = reactive({}); // 记录每辆车最后一次收到消息的时间戳
let heartbeatTimer = null; // 心跳检查定时器
const reconnectTimeout = ref(null);
const reconnectInterval = 5000; // 重连间隔时间（毫秒）
const maxReconnectAttempts = 5; // 最大重连尝试次数
const reconnectAttempts = ref(0);

// 启动心跳检查，检查车辆是否在30秒内收到消息
const startHeartbeatCheck = () => {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
  }
  // 每5秒检查一次
  heartbeatTimer = setInterval(() => {
    const now = Date.now();
    vehicleMarkers.value.forEach((marker, vehicleId) => {
      const lastTime = lastMessageTime[vehicleId];
      if (lastTime) {
        // 如果超过30秒没有收到消息，将状态圆点改为灰色，车速显示为"-"
        if (now - lastTime > 30000) {
          const content = marker.getContent();
          if (content) {
            const statusDot = content.querySelector('span');
            if (statusDot) {
              statusDot.style.backgroundColor = '#d9d9d9'; // 灰色，表示超时
              statusDot.style.display = 'inline-block';
            }
          }
        }
      }
    });
  }, 5000);
};

// 生成WebSocket连接ID
const generateWebSocketId = () => {
  // 使用当前时间戳和随机数生成唯一ID
  return `vehicle_monitor_${Date.now()}_${Math.floor(Math.random() * 10000)}`;
}

// 停止心跳检查
const stopHeartbeatCheck = () => {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
};

// 清除地图上当前选中车辆的任务路线覆盖物
const clearTaskRouteOverlays = () => {
  if (!map.value) return;
  taskRouteOverlays.forEach((overlay) => {
    try { map.value.remove(overlay); } catch (e) { /* ignore */ }
  });
  taskRouteOverlays.length = 0;
};

// 绘制车辆当前任务的路线和起终点/途经点
const drawCurrentTaskRoute = (task) => {
  if (!map.value || !AMapInstance || !task) return;
  clearTaskRouteOverlays();

  const routeOverlays = [];
  const paths = task.paths || [];

  if (paths.length > 0) {
    // 主路线（蓝色粗线 + 白色外发光底层，与 detail.vue 保持一致）
    const polylineBg = new AMapInstance.Polyline({
      path: paths,
      strokeColor: '#ffffff',
      strokeWeight: 10,
      strokeOpacity: 0.6,
      lineJoin: 'round',
      lineCap: 'round',
      zIndex: 1,
    });
    const polyline = new AMapInstance.Polyline({
      path: paths,
      strokeColor: '#1890ff',
      strokeWeight: 6,
      strokeOpacity: 0.9,
      lineJoin: 'round',
      lineCap: 'round',
      showDir: true,
    });
    polylineBg.setMap(map.value);
    polyline.setMap(map.value);
    taskRouteOverlays.push(polylineBg, polyline);
    routeOverlays.push(polyline);
  }

  // 起点（绿色）
  const originCoord = task.originStopGcj02Coordinates;
  const originName = (task.originStopName || '').trim();
  if (originCoord && Array.isArray(originCoord) && originCoord.length === 2) {
    const marker = new AMapInstance.Marker({
      position: originCoord,
      content: `<div style="display:flex;align-items:center;gap:4px;white-space:nowrap;"><div style="width:28px;height:28px;border-radius:50%;background:#52c41a;color:#fff;font-size:12px;line-height:28px;text-align:center;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);">起</div><span style="font-size:12px;color:#333;background:rgba(255,255,255,0.9);padding:2px 6px;border-radius:4px;box-shadow:0 1px 4px rgba(0,0,0,0.2);">${originName}</span></div>`,
      offset: new AMapInstance.Pixel(-14, -14),
    });
    marker.setMap(map.value);
    taskRouteOverlays.push(marker);
    routeOverlays.push(marker);
  }

  // 终点（红色）
  const destCoord = task.destinationStopGcj02Coordinates;
  const destName = (task.destinationStopName || '').trim();
  if (destCoord && Array.isArray(destCoord) && destCoord.length === 2) {
    const marker = new AMapInstance.Marker({
      position: destCoord,
      content: `<div style="display:flex;align-items:center;gap:4px;white-space:nowrap;"><div style="width:28px;height:28px;border-radius:50%;background:#ff4d4f;color:#fff;font-size:12px;line-height:28px;text-align:center;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);">终</div><span style="font-size:12px;color:#333;background:rgba(255,255,255,0.9);padding:2px 6px;border-radius:4px;box-shadow:0 1px 4px rgba(0,0,0,0.2);">${destName}</span></div>`,
      offset: new AMapInstance.Pixel(-14, -14),
    });
    marker.setMap(map.value);
    taskRouteOverlays.push(marker);
    routeOverlays.push(marker);
  }

  // 途经点（蓝色带序号）
  const waypoints = task.waypoints || [];
  waypoints.forEach((wp, idx) => {
    const coord = wp.gcj02Coordinates;
    const wpName = (wp.stopName || wp.name || '').trim();
    if (coord && Array.isArray(coord) && coord.length === 2) {
      const marker = new AMapInstance.Marker({
        position: coord,
        content: `<div style="display:flex;align-items:center;gap:4px;white-space:nowrap;"><div style="width:24px;height:24px;border-radius:50%;background:#1890ff;color:#fff;font-size:12px;line-height:24px;text-align:center;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);">${idx + 1}</div><span style="font-size:12px;color:#333;background:rgba(255,255,255,0.9);padding:2px 6px;border-radius:4px;box-shadow:0 1px 4px rgba(0,0,0,0.2);">${wpName}</span></div>`,
        offset: new AMapInstance.Pixel(-12, -12),
      });
      marker.setMap(map.value);
      taskRouteOverlays.push(marker);
      routeOverlays.push(marker);
    }
  });

  // 有路线数据时自适应视野
  if (routeOverlays.length > 0) {
    map.value.setFitView(routeOverlays, true, [80, 80, 80, 80]);
  }
};

const highlightVehicleOnMap = async (vehicle) => {

  console.log("selectedVehicle", vehicle);
  const vehicleId = vehicle.vehicleId;
  selectedVehicleId.value = vehicleId;

  try {
    // 调用接口获取车辆详情信息
    const res = await getVehicleInfo(vehicleId);
    if (res.code === 200) {
      selectedVehicleInfo.value = res.data;
    }
  } catch (error) {
    console.error('获取车辆详情失败:', error);
    selectedVehicleInfo.value = vehicle;
  }
  
  // 默认选中前方的视角，并加载播放前方视频（使用带重试机制的初始化）
  videoLoading.value = true;
  videoRetryCount.value = 0;
  selectedVideoPosition.value = 'front';
  await nextTick();
  await initPlayerWithRetry();

  // 如果有地图实例和车辆位置，才进行地图定位
  if (map.value) {
    // 如果车辆有位置信息，移动地图中心到车辆位置
    if (vehicle.location) {
      map.value.setCenter(vehicle.location);
      map.value.setZoom(15);
    } else {
      // 如果没有位置信息，尝试从地图标记中获取
      const marker = vehicleMarkers.value.get(vehicleId);
      if (marker) {
        const position = marker.getPosition();
        if (position) {
          map.value.setCenter(position);
          map.value.setZoom(15);
        }
      }
    }

    highlightVehicleMarker(vehicleId);
  }

  // 加载并绘制车辆当前任务的路线
  try {
    const taskRes = await getCurrentTaskByVehcileId(vehicleId);
    if (taskRes.code === 200 && taskRes.data) {
      drawCurrentTaskRoute(taskRes.data);
    } else {
      clearTaskRouteOverlays();
    }
  } catch (error) {
    console.error('获取车辆当前任务失败:', error);
    clearTaskRouteOverlays();
  }
}

// 更新车辆统计信息
const updateVehicleStats = async () => {
  try {
    // 使用接口获取在线车辆数量
    const res = await countVehicleOnlineStatus();
    if (res.code === 200) {
      onlineVehicleCount.value = res.data || 0;
    }
    // 异常车辆数仍基于车辆列表中的状态字段
    errorVehicleCount.value = vehicles.value.filter(v => v.status === -1).length;
  } catch (error) {
    console.error('获取在线车辆数量失败:', error);
  }
}

// 车辆标记缓存，用于快速查找和更新
const vehicleMarkers = ref(new Map())

// 低缩放级别聚合展示配置
const CLUSTER_MAX_ZOOM = 14 // 缩放级别小于等于该值时，按区域聚合显示车辆总数
const CLUSTER_GRID_SIZE = 100 // 聚合网格大小（像素）
const clusterMarkers = [] // 聚合标记集合
const vehicleDataMap = new Map() // 车辆最新数据，用于聚合统计
let vehicleRenderTimer = null // 车辆展示重绘防抖定时器

// 初始化WebSocket连接
const initWebSocket = () => {
  if (ws.value) {
    ws.value.close();
  }

  try {
    // 生成唯一的WebSocket连接ID
    const wsId = generateWebSocketId();

    // 根据环境变量构建WebSocket连接URL
    let url = "";
    console.log(process.env, process.env.VUE_APP_WS_URL);
    if (process.env.VUE_APP_ENV === "development") {
      console.log(window.location);
      url = process.env.VUE_APP_WS_URL + "/ws/" + wsId;
    } else {
      url = "wss://" + window.location.host + "/ws/" + wsId;
    }

    wsUrl.value = url;
    ws.value = new WebSocket(url);
    wsConnected.value = false;

    // 连接建立事件
    ws.value.onopen = () => {
      console.log('WebSocket连接已建立');
      wsConnected.value = true;
      reconnectAttempts.value = 0;
    }

    // 接收消息事件
    ws.value.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        handleVehicleLocationUpdate(data);
      } catch (error) {
        console.error('解析WebSocket消息失败:', error)
      }
    }

    // 错误事件
    ws.value.onerror = (error) => {
      console.error('WebSocket错误:', error);
      wsConnected.value = false;
    }

    // 连接关闭事件
    ws.value.onclose = (event) => {
      console.log('WebSocket连接已关闭:', event.code, event.reason);
      wsConnected.value = false;
      attemptReconnect();
    }
  } catch (error) {
    console.error('创建WebSocket连接失败:', error)
    attemptReconnect();
  }
}

// 处理车辆位置更新
const handleVehicleLocationUpdate = (data) => {
  if (!data || !data.vehicleId || !data.location) {
    console.warn('无效的车辆位置数据:', data)
    return
  }

  // 记录收到消息的时间戳
  lastMessageTime[data.vehicleId] = Date.now();
  // 记录车辆最新数据，用于低缩放级别聚合统计
  vehicleDataMap.set(data.vehicleId, data);

  // 直接在地图上处理推送的车辆数据，不依赖车辆列表
  const marker = vehicleMarkers.value.get(data.vehicleId);
  if (marker) {
    // 如果标记已存在，更新位置
    marker.setPosition(data.location);
    
    // 更新状态指示圆点的颜色
    const content = marker.getContent();
    if (content) {
      const statusDot = content.querySelector('span');
      if (statusDot) {
        if (data.status === 1) {
          statusDot.style.backgroundColor = '#52c41a'; // 绿色，表示正常
          statusDot.style.display = 'inline-block';
        } else if (data.status === -1) {
          statusDot.style.backgroundColor = '#ff4d4f'; // 红色，表示异常
          statusDot.style.display = 'inline-block';
        } else {
          statusDot.style.display = 'none'; // 其他状态不显示
        }
      }
      
      // 更新车辆图标角度
      const iconElement = content.querySelector('img');
      if (iconElement) {
        if (data.angle !== undefined && data.angle !== null) {
          iconElement.style.transform = `rotate(${data.angle + 150}deg)`;
          iconElement.style.transformOrigin = 'center center';
        } else {
          iconElement.style.transform = '';
        }
      }
      
      // 更新状态圆点
      const labelElement = content.querySelector('div:first-child');
      if (labelElement) {
        const statusDot = labelElement.querySelector('span');
        if (statusDot) {
          if (data.status === 1) {
            statusDot.style.backgroundColor = '#52c41a'; // 绿色，表示正常
            statusDot.style.display = 'inline-block';
          } else if (data.status === -1) {
            statusDot.style.backgroundColor = '#ff4d4f'; // 红色，表示异常
            statusDot.style.display = 'inline-block';
          } else {
            statusDot.style.display = 'none'; // 其他状态不显示
          }
        }
      }
    }
  } else {

    // 创建自定义HTML内容，同时包含车辆图标和编号
    //const content = createMarkerContent(data, false);
    const content = document.createElement('div')
    content.style.display = 'flex'
    content.style.alignItems = 'flex-start'
    content.style.width = '120px'
    content.style.verticalAlign = 'top'

    // 创建车辆图标元素
    const iconElement = document.createElement('img')
    iconElement.src = carIcon
    iconElement.style.width = '34px'
    iconElement.style.height = '34px'
    iconElement.style.marginRight = '6px'
    iconElement.style.verticalAlign = 'top'
    
    // 根据angle值旋转车辆图标，angle为车头正向的偏向角度
    if (data.angle !== undefined && data.angle !== null) {
      iconElement.style.transform = `rotate(${data.angle + 150}deg)`;
      iconElement.style.transformOrigin = 'center center';
    }

    // 创建状态指示圆点
    const statusDot = document.createElement('span')
    statusDot.style.display = 'inline-block'
    statusDot.style.width = '8px'
    statusDot.style.height = '8px'
    statusDot.style.borderRadius = '50%'
    statusDot.style.marginRight = '4px'
    statusDot.style.verticalAlign = 'middle'
    
    // 根据状态设置颜色
    if (data.status === 1) {
      statusDot.style.backgroundColor = '#52c41a' // 绿色，表示正常
    } else if (data.status === -1) {
      statusDot.style.backgroundColor = '#ff4d4f' // 红色，表示异常
    } else {
      statusDot.style.display = '#d9d9d9' // 其他状态，显示灰色
    }

    // 创建车辆信息显示区域容器
    const labelElement = document.createElement('div')
    labelElement.style.background = 'rgba(255, 255, 255, 0.8)' // 白色背景，80%透明度
    labelElement.style.padding = '4px 8px'
    labelElement.style.borderRadius = '4px'
    labelElement.style.fontSize = '12px'
    labelElement.style.whiteSpace = 'nowrap'
    labelElement.style.border = 'none'
    labelElement.style.outline = 'none'
    labelElement.style.boxShadow = 'none'
    labelElement.style.margin = '0px'
    labelElement.style.verticalAlign = 'top'
    labelElement.style.lineHeight = 'normal'
    labelElement.style.display = 'inline-block'
    labelElement.style.textAlign = 'center'
    
    // 创建车牌显示元素
    const plateElement = document.createElement('div')
    plateElement.style.display = 'flex'
    plateElement.style.alignItems = 'center'
    plateElement.style.justifyContent = 'center'
    
    // 将状态圆点和车辆编号添加到车牌元素
    plateElement.appendChild(statusDot)
    plateElement.appendChild(document.createTextNode(data.licenceNo || (data.vehicleVin ? data.vehicleVin.slice(-6) : '')))
    
    // 将车牌添加到标签容器
    labelElement.appendChild(plateElement)

    // 将图标和标签添加到容器，调整添加顺序确保labelElement在视觉上更接近顶部
    content.appendChild(labelElement)
    content.appendChild(iconElement)

    //调整元素样式，确保labelElement在容器顶部显示
    labelElement.style.order = '-1'

    // 创建标记（是否显示到地图由当前缩放级别决定）
    const newMarker = new window.AMap.Marker({
      position: data.location,
      title: `${data.licenceNo || ''} - ${data.vehicleVin || ''}`,
      map: null,
      extData: { vehicleId: data.vehicleId, vehicleData: data }, // 存储车辆ID和完整数据用于后续查找
      content: content, // 使用自定义HTML内容
      //anchor: 'center', // 使用内置锚点定位，中心对准经纬度位置，
      offset: new window.AMap.Pixel(-108, -16),
    });

    // 添加点击事件
    newMarker.on('click', () => {
      showVehicleInfoWindow(data, newMarker);
    });

    // 保存标记引用
    vehicleMarkers.value.set(data.vehicleId, newMarker);
  }

  // 根据当前缩放级别重新渲染（聚合/单车位置）
  scheduleVehicleRender();

  // 如果当前推送的车辆是选中的车辆，更新其speed值
  if (selectedVehicleId.value === data.vehicleId && selectedVehicleInfo.value) {
    selectedVehicleInfo.value.status = data.status || 0;
    selectedVehicleInfo.value.speed = data.speed || 0;
    selectedVehicleInfo.value.drivingMode = data.drivingMode || 0;
  }

  // WebSocket连接成功后只启动心跳检查，在线车辆统计由定时器每30秒刷新
}

// 尝试重新连接WebSocket
const attemptReconnect = () => {
  if (reconnectAttempts.value >= maxReconnectAttempts) {
    console.error('达到最大重连尝试次数，停止重连')
    return
  }

  clearTimeout(reconnectTimeout.value)
  reconnectTimeout.value = setTimeout(() => {
    reconnectAttempts.value++
    console.log(`尝试重新连接WebSocket (${reconnectAttempts.value}/${maxReconnectAttempts})`)
    initWebSocket()
  }, reconnectInterval)
}

// 关闭WebSocket连接
const closeWebSocket = () => {
  clearTimeout(reconnectTimeout.value)
  if (ws.value) {
    ws.value.close()
    ws.value = null
  }
}

// 显示车辆信息窗口
const showVehicleInfoWindow = async (vehicle, marker) => {

  if (!vehicle || !map.value) return;
  const vehicleId = vehicle.vehicleId;
  // 更新选中的车辆ID
  selectedVehicleId.value = vehicleId;
  
  try {
    // 调用接口获取车辆详情信息
    const res = await getVehicleInfo(vehicleId);
    if (res.code === 200) {
      selectedVehicleInfo.value = res.data;
      
      // 默认选中前方的视角，并加载播放前方视频（使用带重试机制的初始化）
      videoLoading.value = true;
      videoRetryCount.value = 0;
      selectedVideoPosition.value = 'front';
      await nextTick();
      await initPlayerWithRetry();
    }
  } catch (error) {
    console.error('获取车辆详情失败:', error);
  }
  
  // 如果有地图实例和车辆位置，进行地图定位
  if (map.value) {
    // 如果车辆有位置信息，移动地图中心到车辆位置
    if (vehicle.location) {
      map.value.setCenter(vehicle.location);
      map.value.setZoom(15);
    } else if (marker) {
      // 如果没有位置信息，从地图标记中获取
      const position = marker.getPosition();
      if (position) {
        map.value.setCenter(position);
        map.value.setZoom(15);
      }
    }
  }
  
  // 高亮显示车辆标记
  highlightVehicleMarker(vehicleId);

  // 加载并绘制车辆当前任务的路线
  try {
    const taskRes = await getCurrentTaskByVehcileId(vehicleId);
    if (taskRes.code === 200 && taskRes.data) {
      drawCurrentTaskRoute(taskRes.data);
    } else {
      clearTaskRouteOverlays();
    }
  } catch (error) {
    console.error('获取车辆当前任务失败:', error);
    clearTaskRouteOverlays();
  }
};

// 清除地图上的所有车辆标记
const clearVehicleMarkers = () => {
  if (!window.AMap) return;
  
  vehicleMarkers.value.forEach(marker => {
    if (marker && map.value) {
      map.value.remove(marker);
    }
  });
  vehicleMarkers.value.clear();
  clearClusterMarkers();
  vehicleDataMap.clear();
  
  // 在线车辆统计由定时器每30秒刷新，此处不再调用
};

// 创建聚合标记的HTML内容（区域内车辆总数）
const createClusterContent = (count) => {
  const div = document.createElement('div')
  div.style.display = 'flex'
  div.style.alignItems = 'center'
  div.style.justifyContent = 'center'
  div.style.minWidth = '46px'
  div.style.height = '46px'
  div.style.padding = '0 10px'
  div.style.boxSizing = 'border-box'
  div.style.borderRadius = '23px'
  div.style.background = count >= 50 ? 'rgba(250, 84, 28, 0.86)' : 'rgba(22, 119, 255, 0.86)'
  div.style.border = '2px solid rgba(255, 255, 255, 0.8)'
  div.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.18)'
  div.style.fontWeight = '600'
  div.style.color = '#ffffff'
  div.style.whiteSpace = 'nowrap'
  div.style.cursor = 'pointer'
  // 以内容中心为锚点，避免宽度变化导致偏移
  div.style.transform = 'translate(-50%, -50%)'
  div.innerHTML = `<span style="font-size:${count > 99 ? '13px' : '16px'};line-height:1;">${count}</span><span style="font-size:12px;line-height:1;margin-left:2px;">辆</span>`
  return div
}

// 清除聚合标记
const clearClusterMarkers = () => {
  if (!map.value) return
  clusterMarkers.forEach((marker) => {
    try { map.value.remove(marker) } catch (e) { /* ignore */ }
  })
  clusterMarkers.length = 0
}

// 按当前视野网格聚合车辆，显示区域内车辆总数
const renderClusterMarkers = () => {
  if (!map.value || !window.AMap) return
  clearClusterMarkers()

  const cells = new Map()
  vehicleDataMap.forEach((vehicle) => {
    if (!vehicle || !vehicle.location) return
    const pixel = map.value.lngLatToContainer(vehicle.location)
    if (!pixel) return
    const x = typeof pixel.getX === 'function' ? pixel.getX() : pixel.x
    const y = typeof pixel.getY === 'function' ? pixel.getY() : pixel.y
    const key = `${Math.floor(x / CLUSTER_GRID_SIZE)}:${Math.floor(y / CLUSTER_GRID_SIZE)}`
    if (!cells.has(key)) {
      cells.set(key, { count: 0, lng: 0, lat: 0 })
    }
    const cell = cells.get(key)
    cell.count += 1
    cell.lng += Number(vehicle.location[0])
    cell.lat += Number(vehicle.location[1])
  })

  const currentZoom = map.value.getZoom()
  cells.forEach((cell) => {
    if (!cell.count) return
    const position = [cell.lng / cell.count, cell.lat / cell.count]
    const marker = new window.AMap.Marker({
      position,
      content: createClusterContent(cell.count),
      offset: new window.AMap.Pixel(0, 0),
      map: map.value,
      zIndex: 200,
    })
    // 点击聚合点放大地图，查看具体车辆位置
    marker.on('click', () => {
      map.value.setZoomAndCenter(Math.min(currentZoom + 2, 18), position)
    })
    clusterMarkers.push(marker)
  })
}

// 根据缩放级别切换「区域车辆总数」与「车辆位置」展示
const renderVehiclesByZoom = () => {
  if (!map.value || !window.AMap) return
  const zoom = map.value.getZoom()
  if (zoom <= CLUSTER_MAX_ZOOM) {
    // 低缩放级别：隐藏单车标记，展示聚合数量
    vehicleMarkers.value.forEach((marker) => {
      try { marker.setMap(null) } catch (e) { /* ignore */ }
    })
    renderClusterMarkers()
  } else {
    // 高缩放级别：清除聚合标记，展示具体车辆位置
    clearClusterMarkers()
    vehicleMarkers.value.forEach((marker) => {
      try { marker.setMap(map.value) } catch (e) { /* ignore */ }
    })
  }
}

// 防抖重绘，避免车辆位置高频推送时重复渲染
const scheduleVehicleRender = () => {
  clearTimeout(vehicleRenderTimer)
  vehicleRenderTimer = setTimeout(() => {
    renderVehiclesByZoom()
  }, 200)
}

// 创建普通状态的标签HTML
const createNormalLabel = (vehicle) => {
  const label = document.createElement('div')
  label.style.background = 'rgba(255, 255, 255, 0.8)' // 白色背景，80%透明度
  label.style.color = 'black'
  label.style.padding = '4px 8px'
  label.style.borderRadius = '4px'
  label.style.fontSize = '12px'
  label.style.whiteSpace = 'nowrap'
  label.style.border = 'none'
  label.style.outline = 'none'
  label.style.boxShadow = 'none'
  label.style.margin = '0px'
  label.style.verticalAlign = 'top'
  label.style.lineHeight = 'normal'
  label.style.display = 'inline-block'
  label.style.marginRight = '6px'
  label.style.textAlign = 'center'
  
  // 创建车牌显示元素
  const plateElement = document.createElement('div')
  plateElement.style.display = 'flex'
  plateElement.style.alignItems = 'center'
  plateElement.style.justifyContent = 'center'
  
  // 创建状态指示圆点
  const statusDot = document.createElement('span')
  statusDot.style.display = 'inline-block'
  statusDot.style.width = '8px'
  statusDot.style.height = '8px'
  statusDot.style.borderRadius = '50%'
  statusDot.style.marginRight = '4px'
  statusDot.style.verticalAlign = 'middle'
  
  // 根据状态设置颜色
  if (vehicle && vehicle.status === 1) {
    statusDot.style.backgroundColor = '#52c41a' // 绿色，表示正常
  } else if (vehicle && vehicle.status === -1) {
    statusDot.style.backgroundColor = '#ff4d4f' // 红色，表示异常
  } else {
    statusDot.style.display = 'none' // 其他状态不显示
  }

  // 将状态圆点和车辆编号添加到车牌元素
  plateElement.appendChild(statusDot)
  plateElement.appendChild(document.createTextNode(vehicle?.licenceNo || (vehicle?.vehicleVin ? vehicle.vehicleVin.slice(-6) : '')))
  
  // 将车牌添加到标签容器
  label.appendChild(plateElement)
  
  return label;
};

// 创建高亮状态的标签HTML
const createHighlightLabel = (vehicle) => {
  const label = document.createElement('div')
  label.style.background = 'rgba(24, 144, 255, 0.7)' // 蓝色背景，70%透明度
  label.style.color = 'black'
  label.style.padding = '4px 8px'
  label.style.borderRadius = '4px'
  label.style.fontSize = '12px'
  label.style.whiteSpace = 'nowrap'
  label.style.margin = '0px'
  label.style.verticalAlign = 'top'
  label.style.lineHeight = 'normal'
  label.style.display = 'inline-block'
  label.style.marginRight = '6px'
  label.style.textAlign = 'center'
  
  // 创建车牌显示元素
  const plateElement = document.createElement('div')
  plateElement.style.display = 'flex'
  plateElement.style.alignItems = 'center'
  plateElement.style.justifyContent = 'center'
  
  // 创建状态指示圆点
  const statusDot = document.createElement('span')
  statusDot.style.display = 'inline-block'
  statusDot.style.width = '8px'
  statusDot.style.height = '8px'
  statusDot.style.borderRadius = '50%'
  statusDot.style.marginRight = '4px'
  statusDot.style.verticalAlign = 'middle'
  
  // 根据状态设置颜色
  if (vehicle && vehicle.status === 1) {
    statusDot.style.backgroundColor = '#52c41a' // 绿色，表示正常
  } else if (vehicle && vehicle.status === -1) {
    statusDot.style.backgroundColor = '#ff4d4f' // 红色，表示异常
  } else {
    statusDot.style.display = 'none' // 其他状态不显示
  }

  // 将状态圆点和车辆编号添加到车牌元素
  plateElement.appendChild(statusDot)
  plateElement.appendChild(document.createTextNode(vehicle?.licenceNo || (vehicle?.vehicleVin ? vehicle.vehicleVin.slice(-6) : '')))
  
  // 将车牌添加到标签容器
  label.appendChild(plateElement)
  
  return label;
};

// 高亮显示特定车辆标记
const highlightVehicleMarker = (vehicleId) => {
  if (!map.value || !window.AMap) return;
  
  // 先获取当前选中的标记，保存其位置以便后续恢复
  const selectedMarker = vehicleMarkers.value.get(vehicleId);
  const selectedPosition = selectedMarker ? selectedMarker.getPosition() : null;
  
  // 优化：只更新必要的标记样式，而不是全部重置
  vehicleMarkers.value.forEach((marker, id) => {
    if (!marker) return;
    
    const isSelected = id === vehicleId;
    
    // 更新图标
    const iconPath = isSelected ? carSelect : carIcon;
    marker.setIcon(new window.AMap.Icon({
      size: new window.AMap.Size(34, 34),
      imageSize: new window.AMap.Size(34, 34),
      image: iconPath
    }));
    
    // 优先从标记的extData中获取车辆数据，其次从vehicles列表中查找
    const extData = marker.getExtData ? marker.getExtData() : null;
    const vehicleFromExtData = extData?.vehicleData;
    const vehicleFromList = vehicles.value.find(v => v.id === id || v.vehicleId === id);
    const vehicle = vehicleFromExtData || vehicleFromList;
    
    // 创建容器
    const content = document.createElement('div')
    content.style.position = 'absolute'
    content.style.width = '150px'
    content.style.height = 'auto'
    content.style.display = 'flex'
    content.style.alignItems = 'flex-start'
    
    // 根据是否选中创建不同样式的标签
    const label = isSelected ? createHighlightLabel(vehicle) : createNormalLabel(vehicle);
    
    //添加车辆图标
    const icon = document.createElement('img')
    icon.src = iconPath
    icon.style.width = '34px'
    icon.style.height = '34px'

    // 将标签和图标添加到容器，标签在左侧，图标在右侧
    content.appendChild(label)
    content.appendChild(icon)
    
    // 设置自定义HTML内容
    marker.setContent(content);
  });
  
  // 如果选中的标记存在且有位置，确保它在最上层
  if (selectedMarker && selectedPosition) {
    selectedMarker.setPosition(selectedPosition);
    selectedMarker.setzIndex(1000);
  }
};

// 初始化地图
const drawMap = async () => {
  try {
    // 设置加载状态
    loading.value = true;
    errorMessage.value = null;

    // 清空之前的地图数据和覆盖物
    mapData.value = [];
    clearMap();

    // 等待DOM更新完成，确保容器尺寸已经确定
    await nextTick()

    // 检查地图容器是否存在
    const mapContainer = document.getElementById('monitor-map-container');
    if (!mapContainer) {
      throw new Error('地图容器未找到');
    }

    // 检查地图容器尺寸是否有效
    if (mapContainer.offsetWidth === 0 || mapContainer.offsetHeight === 0) {
      // 如果容器尺寸为0，设置一个临时的最小高度
      mapContainer.style.minHeight = '400px';
      await nextTick();
    }

    // 检查高德地图API是否可用
    const AMap = await AMapLoader.load({
      key: '6588d9e5b6ae0751c799cb4c7b3e30be',
      version: '2.0',
      plugins: ['AMap.ToolBar', 'AMap.Scale', 'AMap.Geolocation', 'AMap.MouseTool', 'AMap.PolyEditor']
    }).catch(error => {
      throw new Error(`高德地图API加载失败: ${error.message || error}`);
    });

    AMapInstance = AMap;

    // 创建地图实例 - 使用2D视图，不启用3D模式
    map.value = new AMap.Map('monitor-map-container', {
      zoom: 6,
      mapStyle: currentMapStyle.value,
      center: [103.8349, 37.3980], // 甘肃省中心点
      viewMode: '2D', // 使用2D视图
      zooms: [2, 20],
      // 开启地图拖拽惯性
      dragEnable: true,
      // 启用平滑动画效果
      animateEnable: true,
      // 显示缩放控件
      showZoomControl: false, // 不显示默认控件，后面手动添加自定义控件
      // 显示室内地图
      showIndoorMap: false
    })

    // 添加自定义地图控件
    addMapControls(AMap);

    // 缩放级别变化时，切换「区域车辆总数」与「具体车辆位置」展示
    map.value.on('zoomend', () => {
      renderVehiclesByZoom();
    });

    // 低缩放级别下平移地图时，重新按视野聚合车辆
    map.value.on('moveend', () => {
      if (map.value && map.value.getZoom() <= CLUSTER_MAX_ZOOM) {
        scheduleVehicleRender();
      }
    });

    // 添加地图加载完成事件
    map.value.on('complete', () => {
      console.log('甘肃省2D地图加载完成')

      // 地图加载完成后加载边界数据
      loadGansuData().catch(error => {
        console.error('加载甘肃省数据失败，使用模拟数据作为备选:', error);
      }).finally(() => {
        loading.value = false;
        // 在线车辆统计由定时器每30秒刷新，此处不再调用
        // 绘制所有测试区域
        drawAllTestAreas();
      });
    })

    // 监听地图错误事件
    map.value.on('error', (e) => {
      console.error('地图错误:', e);
      errorMessage.value = '地图加载过程中发生错误';
      loading.value = false;
    });

    // 监听窗口大小变化，自动调整地图尺寸
    const handleResize = () => {
      if (map.value) {
        try {
          map.value.resize();
        } catch (error) {
          console.warn('地图尺寸调整失败:', error);
        }
      }
    }

    window.addEventListener('resize', handleResize)

    // 在组件销毁时移除事件监听
    onBeforeUnmount(() => {
      window.removeEventListener('resize', handleResize)
    })

  } catch (error) {
    console.error('地图初始化失败:', error);
    errorMessage.value = error.message || '地图初始化失败，请刷新页面重试';
    loading.value = false;

    // 显示错误提示
    areaDescription.value = {
      title: '错误',
      description: errorMessage.value
    }
  }
}

// 添加地图控件
const addMapControls = (AMap) => {
  if (!map.value || !AMap) return;

  // 添加工具条控件
  map.value.addControl(new window.AMap.ToolBar({
    position: 'RB', // 右下角
    offset: [10, 60],
    liteStyle: true // 简洁风格
  }));

  // 添加比例尺控件
  map.value.addControl(new window.AMap.Scale({
    position: 'RB',
    offset: [10, 10]
  }));

  // 添加定位控件（可选）
  map.value.addControl(new window.AMap.Geolocation({
    position: 'RB',
    offset: [10, 110],
    zoomToAccuracy: true,
    showCircle: false
  }));
}


const createPolygon = (feature) => {
  try {
    // 处理坐标数据格式
    let coordinates = feature.geometry.coordinates

    // 坐标格式验证和转换
    if (!coordinates || !Array.isArray(coordinates)) {
      console.warn('无效的坐标数据:', feature);
      return null;
    }

    // 如果是多层嵌套的数组，提取最内层坐标
    if (Array.isArray(coordinates[0]) &&
        Array.isArray(coordinates[0][0]) &&
        Array.isArray(coordinates[0][0][0])) {
      coordinates = coordinates[0]
    }

    // 确保坐标是合法的经纬度数组
    if (!Array.isArray(coordinates[0]) ||
        coordinates[0].length !== 2 ||
        typeof coordinates[0][0] !== 'number') {
      console.warn('坐标格式不正确:', coordinates);
      return null;
    }

    // 城市属性信息
    const properties = feature.properties || {}

    // 创建多边形 - 只保留边界线，不使用填充颜色，边界线使用灰色
    const polygon = new window.AMap.Polygon({
      path: coordinates,
      zIndex: 50,
      bubble: true,
      strokeColor: '#999999',
      strokeOpacity: 0.7,
      strokeWeight: 1,
      fillOpacity: 0,
      strokeStyle: "solid",
      // 启用鼠标悬停交互
      cursor: 'pointer',
      // 过渡动画
      transition: 'all 0.3s ease'
    })

    // 绑定鼠标悬停事件 - 高亮显示
    polygon.on('mouseover', () => {
      // 存储原始样式以便恢复
      polygon._originalStyle = {
        strokeColor: polygon.getOptions().strokeColor,
        strokeWeight: polygon.getOptions().strokeWeight,
        fillOpacity: polygon.getOptions().fillOpacity
      };

      // 应用高亮样式
      polygon.setOptions({
        strokeColor: '#1E8EF2',
        strokeWeight: 2,
        fillOpacity: 0.1,
        fillColor: '#1E8EF2'
      });

      // 提升层级
      polygon.setzIndex(100);
    });

    // 绑定鼠标移出事件 - 恢复原始样式
    polygon.on('mouseout', () => {
      if (polygon._originalStyle) {
        polygon.setOptions({
          strokeColor: polygon._originalStyle.strokeColor,
          strokeWeight: polygon._originalStyle.strokeWeight,
          fillOpacity: polygon._originalStyle.fillOpacity
        });

        // 恢复原始层级
        polygon.setzIndex(50);
      }
    });

    // 绑定点击事件 - 添加点击反馈效果
    polygon.on('click', () => {
      // 设置区域描述信息
      areaDescription.value = {
        title: properties.name || '未知区域',
        description: `点击了${properties.name || '未知区域'}`
      };

      // 点击效果 - 短暂高亮后恢复
      polygon.setOptions({
        strokeColor: '#F23218',
        strokeWeight: 3
      });

      setTimeout(() => {
        if (polygon._originalStyle) {
          polygon.setOptions({
            strokeColor: polygon._originalStyle.strokeColor,
            strokeWeight: polygon._originalStyle.strokeWeight
          });
        }
      }, 300);

      // 平滑移动到选中区域
      if (map.value && properties.centroid) {
        map.value.panTo(properties.centroid, {
          duration: 600
        });
      }
    });

    return polygon;
  } catch (error) {
    console.error('创建多边形失败:', error);
    return null;
  }
}

// 清除地图上的所有覆盖物
const clearMap = () => {
  if (polygons.value && polygons.value.length > 0 && map.value) {
    map.value.remove(polygons.value)
    polygons.value = []
  }
  if (polylines.value && polylines.value.length > 0 && map.value) {
    map.value.remove(polylines.value)
    polylines.value = []
  }
  if (markers.value && markers.value.length > 0 && map.value) {
    map.value.remove(markers.value)
    markers.value = []
  }
  
  // 清空车辆标记
  clearVehicleMarkers();
}

// 加载甘肃省地图数据
const loadGansuData = async () => {
  try {
    // 设置加载状态
    loading.value = true
    errorMessage.value = ''

    // 从public文件夹加载甘肃省的边界数据
    const response = await fetch('/gansu.json')

    if (!response.ok) {
      throw new Error(`HTTP错误! 状态: ${response.status}`)
    }

    // 解析JSON数据
    const data = await response.json()

    // 数据格式验证
    if (!data || !Array.isArray(data.features) || data.features.length === 0) {
      throw new Error('甘肃省地图数据格式不正确或为空')
    }

    // 存储地图数据
    mapData.value = data

    // 绘制甘肃省边界
    drawGansuBoundaries(data)

    console.log('甘肃省地图数据加载成功')

    // 返回加载的地图数据
    return data
  } catch (error) {
    console.error('加载甘肃省地图数据失败:', error)
    errorMessage.value = `地图数据加载失败: ${error.message}`
    throw error
  } finally {
    // 无论成功失败，都要设置加载状态为false
    loading.value = false
  }
}

// 绘制甘肃省边界
const drawGansuBoundaries = (data) => {
  try {
    // 确保map实例存在
    if (!map.value) {
      console.error('地图实例未初始化')
      return
    }
    // 清空之前的边界
    clearMap()
    // 获取地图数据中的features数组
    const features = data.features || []

    // 遍历数据，创建边界多边形
    features.forEach(feature => {
      // 验证特征数据格式
      if (!feature || !feature.geometry || feature.geometry.type !== 'Polygon') {
        console.warn('无效的多边形特征数据:', feature)
        return
      }

      // 创建多边形
      const polygon = createPolygon(feature)

      // 添加到多边形数组并在地图上显示
      if (polygon) {
        polygons.value.push(polygon)
        map.value.add(polygon)
      }
    })

    // 调整地图视图以适应所有边界
    if (polygons.value.length > 0) {
      // 计算所有多边形的边界范围
      const bounds = new window.AMap.Bounds()
      polygons.value.forEach(polygon => {
        // 获取多边形路径
        const path = polygon.getPath()
        // 将路径点添加到边界计算中
        path.forEach(point => {
          bounds.extend(point)
        })
      })

      // 调整地图视图以适应边界
      map.value.setFitView(bounds, false, [10, 10, 10, 10])
    }

    console.log('甘肃省边界绘制完成')
  } catch (error) {
    console.error('绘制甘肃省边界失败:', error)
    throw error
  }
}

// 销毁地图
const destroyMap = () => {
  if (map.value) {
    map.value.destroy()
    map.value = null
    console.info("甘肃省地图已销毁")
  }
}

// 生命周期钩子
onMounted(async () => {
  // 优先加载地图，再获取数据
  await drawMap();
  
  // 地图初始化后再获取其他数据
  await fetchTestAreas();
  await fetchCompanyListData();
  await fetchVehicleList();
  
  // 地图初始化后检查是否需要自动选中区域
  checkAutoSelectArea();
  updateVehicleStats();
  initWebSocket();
  await nextTick();
  await initPlayer();
  
  // 启动车辆在线状态定时刷新
  startOnlineStatusRefresh();
  
  // 启动心跳检查
  startHeartbeatCheck();
  
  // 启动业务数据定时刷新（每10分钟）
  businessUpdateTimer = setInterval(() => {
    fetchAllBusinessData();
  }, 10 * 60 * 1000);
})

onBeforeUnmount(() => {
  clearTaskRouteOverlays();
  destroyMap();
  closeWebSocket();
  destroyPlayer();
  clearTimeout(vehicleRenderTimer);
  
  // 停止车辆在线状态定时刷新
  stopOnlineStatusRefresh();
  
  // 停止心跳检查
  stopHeartbeatCheck();
  
  // 停止业务数据定时刷新
  if (businessUpdateTimer) {
    clearInterval(businessUpdateTimer);
    businessUpdateTimer = null;
  }
})
</script>

<style lang="less" scoped>
.monitor-map-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 600px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

#monitor-map-container {
  width: 100%;
  height: 100vh;
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
}

.monitor-content {
  position: relative;
  width: 100%;
  height: 100%;
}

.vehicle-info-container {
  position: absolute;
  right: 20px;
  top: 400px;
  width: 295px;
  background-color: #ffffff;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  overflow: hidden;
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.vehicle-info-section {
  background-color: #ffffff;
  padding: 12px;
}

/* 车辆详细信息布局 */
.vehicle-details {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

/* 左侧车辆图片容器，右上角显示是否在线 */
.vehicle-image-container {
  width: 100px;
  height: 100px;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 在线状态指示器 */
.online-status-indicator {
  position: absolute;
  top: 0;
  right: 0;
  background-color: #666;
  color: white;
  font-size: 10px;
  padding: 2px 6px;
  border-bottom-left-radius: 4px;
  z-index: 10;
}

.online-status-indicator.online {
  background-color: #4CAF50;
}

.online-status-indicator.offline {
  background-color: #d9d9d9;
}

.vehicle-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* 右侧车辆信息内容 */
.vehicle-info-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

/* 时速和驾驶模式容器 */
.speed-mode-container {
  display: flex;
  gap: 8px;
  margin-top: 4px;
  align-items: flex-end;
}

.vehicle-info-item {
  display: flex;
  flex-direction: column;
}

.info-label {
  font-size: 12px;
  color: #666;
  font-weight: 400;
  margin-bottom: 2px;
}

.vehicle-info-value-plate {
  font-size: 14px;
  color: #333;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.vehicle-info-value {
  font-size: 13px;
  color: #333;
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.vehicle-speed-box {
  background-color: #e6f7ff;
  border: 1px solid #91d5ff;
  border-radius: 6px;
  padding: 8px 12px;
  text-align: center;
  position: relative;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 50%;
  display: block;
}

.speed-value {
  font-size: 16px;
  font-weight: 600;
  color: #1890ff;
  margin-bottom: 4px;
  display: inline-flex;
  align-items: baseline;
  justify-content: center;
  width: 100%;
}

.speed-unit {
  font-size: 12px;
  font-weight: normal;
}

.speed-label {
  font-size: 12px;
  color: #666;
  font-weight: 400;
}

.vehicle-mode-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 50%;
  padding: 0px 12px;
  text-align: center;
}

.mode-value {
  font-size: 14px;
  font-weight: bold;
  padding: 2px 8px;
  border-radius: 4px;
  margin-bottom: 4px;
}

.mode-auto {
  background-color: #28a745;
  color: white;
}

.mode-manual {
  background-color: #fd7e14;
  color: white;
}

.mode-remote {
  background-color: #17a2b8;
  color: white;
}

.mode-label {
  font-size: 12px;
  color: #666;
  font-weight: 400;
}

.vehicle-operation-section {
  background-color: #ffffff;
  border-radius: 4px;
  padding: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

/* 测试路线区域样式 */
.route-section {
  margin-top: 0;
}

.route-info {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 7px 24px;
  position: relative;
}

.info-item {
  display: flex;
  align-items: flex-start;
  margin-right: 16px;
  white-space: normal;
  //margin-bottom: 8px;
}

.info-label {
  font-size: 13px;
  color: #606266;
  font-weight: 500;
  margin-right: 6px;
  text-align: right;
  width: 80px;
  flex-shrink: 0;
}

.info-value {
  font-size: 13px;
  color: #303133;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  white-space: normal;
}

.route-name {
  font-size: 14px;
  color: #333;
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
  max-width: 200px;
}

.view-details-btn {
  background: #1890ff;
  color: white;
  border: none;
  padding: 6px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.3s;
  margin-left: auto;
  white-space: nowrap;
  width: 100%;
}

.view-details-btn:hover {
  background-color: #40a9ff;
  border-color: #40a9ff;
}

.operation-area {
  display: flex;
  gap: 12px;
}

/* 左侧联系客服按钮 */
.left-operation {
  flex: 1;
  padding: 5px;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.contact-service-btn {
  width: 80px;
  height: 80px;
  background-color: #f5f5f5;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 6px;
}

.service-icon {
  width: 70px;
  height: 70px;
  vertical-align: middle;
}

.contact-service-btn:hover {
  background-color: #e6f7ff;
  border-color: #1890ff;
  color: #1890ff;
}

/* 右侧各视角按钮 */
.right-operation {
  flex: 2;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  padding: 7px;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.video-position-row {
  display: flex;
  gap: 8px;
  width: 100%;
  justify-content: center;
}

.video-position-center {
  display: flex;
  justify-content: center;
  width: 100%;
}

.video-position-btn {
  padding: 6px 16px;
  background-color: #f5f5f5;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 12px;
  color: #333;
  cursor: pointer;
  transition: all 0.3s;
  min-width: 60px;
}

.video-position-btn:hover {
  background-color: #e6f7ff;
  border-color: #1890ff;
  color: #1890ff;
}

.video-position-btn.active {
  background-color: #1890ff;
  border-color: #1890ff;
  color: white;
}

.search-filter-area {
  position: absolute;
  left: 20px;
  top: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  padding: 4px;
  z-index: 10;
  width: 480px;
}

.search-input-wrapper {
  display: flex;
  gap: 10px;
  align-items: center;
}

.search-input-inner {
  position: relative;
  flex: 1;
  min-width: 0;
  display: flex;
}

.search-input {
  flex: 1;
  width: 100%;
  padding: 8px 28px 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
}

.search-clear {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  line-height: 14px;
  text-align: center;
  border-radius: 50%;
  background: #bfbfbf;
  color: #ffffff;
  font-size: 12px;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: #8c8c8c;
  }
}

.search-btn {
  padding: 8px 20px;
  background-color: #1890ff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  white-space: nowrap;
}

.search-btn:hover {
  background-color: #40a9ff;
}

/* 右上角信息显示区域样式 */
.info-display-area {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 100;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  padding: 4px 16px;
  min-width: 450px;
}
  
.area-info-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.test-area-vehicle-info-item {
  margin-bottom: 0;
}

.test-area-vehicle-info-item label {
  display: inline-block;
  margin-right: 8px;
  font-size: 14px;
  color: #333;
}

.test-area-vehicle-info-item .test-area-label {
  font-size: 14px;
  color: #666;
  font-weight: 500;
  vertical-align: middle;
}

.test-area-vehicle-info-item .test-area-select {
  vertical-align: middle;
}
  
.separator {
  color: #d9d9d9;
  margin: 0 16px;
  display: inline-block;
  width: 1px;
  height: 20px;
  background-color: #d9d9d9;
  vertical-align: middle;
}
  
.info-board-stats {
  display: flex;
  align-items: center;
  gap: 3px;
}

/* 车辆列表区域样式 */
.vehicle-list-area {
  position: absolute;
  left: 20px;
  top: 80px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  padding: 12px;
  min-height: 100px;
  max-height: calc(100vh - 120px);
  width: 370px;
  overflow-y: auto;
  box-sizing: border-box;
  z-index: 10;
}
  
.vehicle-list-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}
  
.vehicle-list-title {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  margin-right: 8px;
}
  
.vehicle-count {
  font-size: 14px;
  color: #999;
}
  
.vehicle-list {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
}
  
.vehicle-item {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
    border: 1px solid transparent;
  }
  
  .vehicle-item-online {
    background: linear-gradient(135deg, rgba(0, 180, 166, 0.4) 0%, rgba(0, 180, 166, 0.55) 100%);
  }
  
  .vehicle-item-offline {
    background: linear-gradient(135deg, rgba(252, 252, 252, 0.95) 0%, rgba(248, 248, 248, 0.98) 100%);
  }
  
  .vehicle-item:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  .vehicle-item-selected {
    border: 2px solid #1890ff;
    box-shadow: 0 2px 8px rgba(24, 144, 255, 0.3);
  }
  
.vehicle-info {
  flex: 1;
}
  
.vehicle-main-info {
  display: grid;
  grid-template-columns: minmax(60px, 70px) 1fr;
  align-items: center;
  gap: 12px;
  margin-bottom: 4px;
}
  
.vehicle-type {
  padding: 2px 8px;
  background: #1890ff;
  color: white;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
}
  
.testing-number {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}
  
.business-status-badge {
  position: absolute;
  top: 0;
  right: 0;
  padding: 4px 12px;
  border-radius: 0 8px 0 8px;
  font-size: 12px;
  font-weight: 500;
}

.vehicle-route {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
}

.vehicle-status-text {
  color: #666;
  display: flex;
  align-items: center;
  gap: 6px;
  
  .status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    
    &.status-online {
      background-color: #52c41a;
    }
    
    &.status-offline {
      background-color: #999;
    }
  }
}

.vehicle-operation-org {
  overflow: hidden;
}

.operation-org-name {
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}

.status-idle {
  background-color: rgba(24, 144, 255, 0.15);
  color: #40a9ff;
}

.status-delivering {
  background-color: rgba(255, 149, 64, 0.15);
  color: #fa8c16;
}

.status-returning {
  background-color: rgba(82, 196, 26, 0.15);
  color: #52c41a;
}
  
.board-stat-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.board-status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}
  
.online-dot {
  background-color: #52c41a;
}
  
.error-dot {
  background-color: #ff4d4f;
}
  
.board-stat-label {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}
  
.board-stat-value {
  font-size: 16px;
  font-weight: bold;
  color: #333;
}

/* 车辆列表分页样式 */
.vehicle-pagination {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

/* 顶层容器样式 */
.monitor-container {
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  margin: 0;
  padding: 0;
}

.map-section {
  width: 100%;
  height: 100%;
  position: relative;
}

.map-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
}

/* 事件区域样式 */
.vehicle-events-container {
  position: absolute;
  top: 80px;
  right: 20px;
  width: 300px;
  z-index: 10;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 15px;
  background-color: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 1px solid #e8e8e8;
}

.event-item {
  display: flex;
  align-items: center;
  flex: 1;
  padding: 10px;
  border-radius: 8px;
  transition: all 0.3s ease;
  cursor: pointer;
}

.event-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.event-icon {
  font-size: 20px;
  margin-right: 10px;
}

.event-content {
  display: flex;
  flex-direction: column;
}

.event-label {
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 2px;
}

.event-count {
  font-size: 22px;
  font-weight: bold;
}

/* 故障事件样式 */
.fault-event {
  background-color: rgba(255, 77, 79, 0.1);
  border: 1px solid rgba(255, 77, 79, 0.2);
}

.fault-event .event-label {
  color: #ff4d4f;
}

.fault-event .event-count {
  color: #ff4d4f;
}

/* 异常事件样式 */
.abnormal-event {
  background-color: rgba(255, 179, 0, 0.1);
  border: 1px solid rgba(255, 179, 0, 0.2);
}

.abnormal-event .event-label {
  color: #ffb300;
}

.abnormal-event .event-count {
  color: #ffb300;
}

/* 车辆视频容器样式 */
.vehicle-video-container {
  position: absolute;
  top: 200px;
  right: 20px;
  width: 300px;
  height: 195px;
  z-index: 10;
  overflow: hidden;
  border-radius: 8px; /* 圆角效果 */
}

.front-video-player {
  width: 100%;
  height: 100%;
}

.video-playback-area {
  width: 100%;
  height: 100%;
  background-color: #000;
  position: relative;
  border-radius: 8px;
  overflow: hidden;
}

.video-player {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
  position: relative;
  z-index: 1;
}

.video-placeholder-front {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background: linear-gradient(45deg, #1a1a1a, #2d2d2d);
  color: #fff;
  position: relative;
}

.video-indicator {
  display: flex;
  justify-content: space-between;
  padding: 8px 12px;
  background-color: rgba(0, 0, 0, 0.5);
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  box-sizing: border-box;
  z-index: 2;
}

.video-position {
  font-size: 14px;
  font-weight: 500;
  color: #fff;
}

.video-status {
  font-size: 12px;
  color: #4CAF50;
  background-color: rgba(76, 175, 80, 0.2);
  padding: 2px 6px;
  border-radius: 4px;
}

.video-timestamp {
  text-align: right;
  padding: 8px 12px;
  font-size: 12px;
  background-color: rgba(0, 0, 0, 0.5);
}

/* 响应式样式 */
@media (max-width: 768px) {
  .search-filter-area,
  .info-display-area {
    min-width: auto;
  }
  
  .search-input-wrapper {
    flex-direction: column;
    align-items: stretch;
  }
  
  .company-select,
  .search-btn {
    width: 100%;
  }
  
  .info-board-stats {
    flex-direction: column;
    gap: 10px;
  }
}

.monitor-map {
  position: relative;
  width: 100%;
  height: 100%;
  flex: 1;
  min-height: 0;
}

#monitor-map-container {
  width: 100%;
  height: 100vh;
  min-height: 100vh;
  transition: all 0.5s ease;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
}


</style>